import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Server, Users, BookOpen, ExternalLink, Flag, Radio, Plus, Loader2, Trash2, ShieldCheck, Wifi, WifiOff, Wrench, ArrowRight, Gamepad2, Globe, X, Star, Search, Activity } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { NcdbDisclaimer } from "@/components/cef/cefShared";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const REGIONS = ["OCE", "NA", "EU", "ASIA", "SA"];
const MODES = ["Hardcore RP", "Casual RP", "Cops & Civs", "PVE", "Custom"];
const AGE_RATINGS = ["13+", "16+", "18+"];
const PLAYSTYLES = ["Hardcore RP", "Story-driven RP", "Cop-focused", "Civ-focused", "New-player friendly", "18+ mature"];
const RENT_URL = "https://www.4netplayers.com/en/gameserver-hosting/new-citizen/";

/* City Pulse — public stats modal for any listed server. */
function StatsModal({ server, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/nc/servers/${server.id}/citystats`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [server.id]);
  const pulse = data?.pulse;
  const ROWS = pulse ? [
    ["Registered citizens", pulse.citizens], ["Properties settled", `${pulse.properties_sold}/${pulse.properties_total}`],
    ["Open 911 calls", pulse.open_911], ["Court cases on the docket", pulse.court_cases],
    ["Buzzer posts", pulse.buzzers], ["Pawn counter listings", pulse.pawn_listings],
  ] : [];
  return (
    <motion.div className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <div data-testid="server-stats-modal" className="w-full max-w-lg border border-nc-blue/40 bg-[#0a0a0a] p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">City Pulse // {server.name}</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {!data ? <p className="font-mono text-[10px] text-zinc-500 animate-pulse">Reading the city's pulse…</p> : (
          <>
            <div className="flex gap-2 flex-wrap">
              <span className="font-mono text-[8px] uppercase border border-white/10 text-zinc-400 px-2 py-1">{data.server.region}</span>
              <span className="font-mono text-[8px] uppercase border border-white/10 text-zinc-400 px-2 py-1">{data.server.mode}</span>
              <span className="font-mono text-[8px] uppercase border border-amber-500/40 text-amber-300 px-2 py-1">Rated {data.server.age_rating}</span>
              {data.server.tags.map((t) => <span key={t} className="font-mono text-[8px] uppercase border border-nc-blue/30 text-nc-blue px-2 py-1">{t}</span>)}
            </div>
            {data.server.snapshot && (
              <div className="border border-white/5 bg-black/30 px-3 py-2 flex gap-3 flex-wrap">
                <span className="font-mono text-[9px] text-zinc-300">{data.server.snapshot.players}/{data.server.snapshot.max_players} in city</span>
                {data.server.snapshot.weather && <span className="font-mono text-[9px] text-zinc-500">{data.server.snapshot.weather}</span>}
                {data.server.snapshot.game_time && <span className="font-mono text-[9px] text-zinc-500">{data.server.snapshot.game_time}</span>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {ROWS.map(([label, val], i) => (
                <div key={i} data-testid={`pulse-${i}`} className="border border-white/10 bg-black/40 px-4 py-3">
                  <p className="font-sans font-bold text-lg text-zinc-50">{val}</p>
                  <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
            {data.server.active_mods.length > 0 && (
              <div>
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">Installed mods</p>
                <div className="flex gap-1.5 flex-wrap">
                  {data.server.active_mods.map((m) => <span key={m} className="font-mono text-[8px] border border-nc-green/40 text-nc-green px-2 py-1">{m}</span>)}
                </div>
              </div>
            )}
            {data.server.discord && <a href={data.server.discord} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] uppercase tracking-[0.2em] text-nc-blue hover:text-zinc-100 flex items-center gap-1.5"><Gamepad2 className="w-3.5 h-3.5" /> Server Discord</a>}
          </>
        )}
      </div>
    </motion.div>
  );
}

function ReportButton({ target, onReport, testid }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await onReport(reason);
      toast.success("Report filed with the moderation team");
      setOpen(false); setReason("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid={testid} onClick={() => setOpen(true)} className="font-mono text-[8px] uppercase tracking-[0.15em] border border-white/15 text-zinc-500 hover:text-red-400 hover:border-red-500/40 px-2 py-1.5 transition-colors flex items-center gap-1"><Flag className="w-3 h-3" /> Report</button>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
            <div data-testid={`${testid}-modal`} className="w-full max-w-md border border-red-500/40 bg-[#0a0a0a] p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-red-400 uppercase">Report {target}</p>
              <textarea data-testid={`${testid}-reason`} rows={4} className={`${inputCls} resize-none`} placeholder="What's wrong? (cheating, abuse, fake server, ToS breach…)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={600} />
              <button data-testid={`${testid}-send`} disabled={busy || reason.trim().length < 10} onClick={send} className="bg-red-500/80 hover:bg-red-500 disabled:opacity-40 text-white font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-3 transition-colors flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} File Report
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function ClaimModal({ server, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [form, setForm] = useState({ totp_code: "", signature_name: "", agree: false });
  const [result, setResult] = useState(null);
  const [confirmErr, setConfirmErr] = useState("");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const r = await api.post(`/nc/servers/${server.id}/claim`);
        setClaim(r.data);
        setStep(2);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        onClose();
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server.id]);

  const verify = async () => {
    setBusy(true); setVerifyMsg("");
    try {
      const r = await api.post(`/nc/servers/${server.id}/claim/verify`);
      if (r.data.verified) { setStep(3); toast.success(`Hostname verified via ${r.data.channel}`); }
      else setVerifyMsg(r.data.detail || "Token not visible yet");
    } catch (err) { setVerifyMsg(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setConfirmErr("");
    try {
      const r = await api.post(`/nc/servers/${server.id}/claim/confirm`, form);
      setResult(r.data);
      onDone();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setConfirmErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <motion.div className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <div data-testid="claim-modal" className="w-full max-w-lg border border-nc-orange/40 bg-[#0a0a0a] p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Claim Server // {server.name}</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1.5">{[1, 2, 3].map((n) => <span key={n} className={`h-0.5 flex-1 ${step >= n ? "bg-nc-orange" : "bg-white/10"}`} />)}</div>

        {step === 1 && <p className="font-mono text-[10px] text-zinc-500 animate-pulse">Generating your verification token…</p>}

        {step === 2 && claim && (
          <>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">Step 1 — prove you control the live server. Put this token in its hostname (expires in {Math.round(claim.expires_in_seconds / 60)} min):</p>
            <code data-testid="claim-token" className="block font-mono text-lg text-nc-orange bg-black border border-nc-orange/40 px-4 py-3 text-center tracking-[0.2em]">{claim.token}</code>
            <div className="flex flex-col gap-1.5">{claim.instructions.map((ins, i) => <p key={i} className="font-mono text-[9px] text-zinc-500 leading-relaxed">▸ {ins}</p>)}</div>
            {verifyMsg && <p data-testid="claim-verify-msg" className="font-mono text-[10px] text-red-400 leading-relaxed border border-red-500/30 bg-red-500/5 p-3">{verifyMsg}</p>}
            <button data-testid="claim-verify-btn" disabled={busy} onClick={verify} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-3 transition-colors flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify Hostname
            </button>
          </>
        )}

        {step === 3 && !result && (
          <>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">Step 2 — hostname verified. Finalize with your authenticator code and the ownership agreement:</p>
            <div className="border border-white/10 bg-black/40 p-4 max-h-36 overflow-y-auto">
              <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">SERVER OWNERSHIP AGREEMENT (NC-SERVER-OWNER-v1): You operate this server under the New Citizen community guidelines. You are responsible for its moderation, lawful content and player data. SpecPixel may delist servers for cheating tools, doxxing or hate content. Your game API key is a secret — leaking it lets others write to your world registry. Monetisation must not sell RP-breaking advantages.</p>
            </div>
            <label className="flex items-center gap-2.5 font-mono text-[10px] text-zinc-300 cursor-pointer">
              <input data-testid="claim-agree" type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} className="accent-nc-orange" /> I accept the Server Ownership Agreement
            </label>
            <input data-testid="claim-signature" className={inputCls} placeholder="Type your full name as signature" value={form.signature_name} maxLength={80} onChange={(e) => setForm({ ...form, signature_name: e.target.value })} />
            <input data-testid="claim-totp" className={inputCls} placeholder="6-digit authenticator code" value={form.totp_code} maxLength={8} onChange={(e) => setForm({ ...form, totp_code: e.target.value })} />
            {confirmErr && <p data-testid="claim-confirm-error" className="font-mono text-[10px] text-red-400 leading-relaxed border border-red-500/30 bg-red-500/5 p-3">{confirmErr}</p>}
            <button data-testid="claim-confirm-btn" disabled={busy || !form.agree || form.signature_name.trim().length < 2 || form.totp_code.trim().length < 6} onClick={confirm} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-3 transition-colors flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sign & Activate Ownership
            </button>
          </>
        )}

        {result && (
          <div data-testid="claim-success" className="flex flex-col gap-3">
            <p className="font-sans font-bold text-nc-green text-sm">{result.message}</p>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">Your server API key (also visible anytime in Admin → NC Database):</p>
            <code data-testid="claim-api-key" className="block font-mono text-[11px] text-zinc-100 bg-black border border-white/15 px-3 py-2.5 break-all">{result.api_key}</code>
            <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">{result.bridge}</p>
            <a href={`/cef/loading/${server.id}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] uppercase tracking-[0.2em] text-nc-blue hover:text-zinc-100 flex items-center gap-1.5">Preview your loading screen <ExternalLink className="w-3 h-3" /></a>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function WhitelistModal({ server, onClose }) {
  const [mine, setMine] = useState(undefined);
  const [form, setForm] = useState({ age_bracket: "18–24", experience: "", why: "", rules_ack: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/nc/servers/${server.id}/whitelist/mine`).then((r) => setMine(r.data.application)).catch(() => setMine(null));
  }, [server.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${server.id}/whitelist`, form);
      setMine(r.data);
      toast.success("Application submitted — the server team will review it");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const STATUS_STYLE = { pending: "text-nc-orange border-nc-orange/40", accepted: "text-nc-green border-nc-green/40", denied: "text-red-400 border-red-500/40" };
  return (
    <motion.div className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <div data-testid="whitelist-modal" className="w-full max-w-lg border border-nc-blue/40 bg-[#0a0a0a] p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">Whitelist Application // {server.name}</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {mine === undefined ? (
          <p className="font-mono text-[10px] text-zinc-500 animate-pulse">Checking your application status…</p>
        ) : mine ? (
          <div data-testid="whitelist-status" className="flex flex-col gap-3">
            <span className={`font-mono text-[10px] uppercase border px-3 py-2 w-fit ${STATUS_STYLE[mine.status]}`}>{mine.status}</span>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
              {mine.status === "pending" && "Your application is in the queue — the server team reviews applications regularly."}
              {mine.status === "accepted" && "You're whitelisted! Your in-game access activates on your next join."}
              {mine.status === "denied" && "This application was declined. You may submit a new one with stronger answers."}
            </p>
            {mine.status === "denied" && <button onClick={() => setMine(null)} className="font-mono text-[9px] uppercase text-nc-blue border border-nc-blue/40 px-3 py-2 w-fit hover:bg-nc-blue/10 transition-colors">Apply again</button>}
          </div>
        ) : (
          <>
            <div><span className={labelCls}>Age bracket</span>
              <select data-testid="wl-age" className={inputCls} value={form.age_bracket} onChange={(e) => setForm({ ...form, age_bracket: e.target.value })}>
                {["16–17", "18–24", "25–34", "35+"].map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div><span className={labelCls}>RP experience *</span><textarea data-testid="wl-experience" rows={3} className={`${inputCls} resize-none`} placeholder="Servers you've played, characters, hours…" value={form.experience} maxLength={1200} onChange={(e) => setForm({ ...form, experience: e.target.value })} /></div>
            <div><span className={labelCls}>Why this server? *</span><textarea data-testid="wl-why" rows={3} className={`${inputCls} resize-none`} placeholder="What will you bring to the city?" value={form.why} maxLength={1200} onChange={(e) => setForm({ ...form, why: e.target.value })} /></div>
            <label className="flex items-center gap-2.5 font-mono text-[10px] text-zinc-300 cursor-pointer">
              <input data-testid="wl-rules" type="checkbox" checked={form.rules_ack} onChange={(e) => setForm({ ...form, rules_ack: e.target.checked })} className="accent-nc-blue" /> I've read and accept the server rules
            </label>
            <button data-testid="wl-submit" disabled={busy || form.experience.trim().length < 20 || form.why.trim().length < 20 || !form.rules_ack} onClick={submit} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-3 transition-colors flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScrollTextIcon />} Submit Application
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}

function ScrollTextIcon() { return <ShieldCheck className="w-4 h-4" />; }

function ServersTab({ me }) {
  const [data, setData] = useState(null);
  const [steam, setSteam] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", region: "OCE", mode: "Hardcore RP", description: "", discord: "", age_rating: "16+", tags: "" });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claimFor, setClaimFor] = useState(null);
  const [wlFor, setWlFor] = useState(null);
  const [statsFor, setStatsFor] = useState(null);
  const [favs, setFavs] = useState([]);
  const [fMode, setFMode] = useState("");
  const [fAge, setFAge] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [fFavsOnly, setFFavsOnly] = useState(false);
  const load = () => api.get("/nc/servers").then((r) => setData(r.data)).catch(() => setData({ servers: [] }));
  useEffect(() => {
    load();
    api.get("/nc/servers/steam").then((r) => setSteam(r.data)).catch(() => setSteam(null));
    const t = setInterval(load, 30000); // live world sync — cards refresh with bridge heartbeats
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (me) api.get("/nc/servers/favorites/mine").then((r) => setFavs(r.data.server_ids)).catch(() => {});
  }, [me]);

  const toggleFav = async (s) => {
    if (!me) { toast.error("Sign in to favourite servers"); return; }
    try {
      const r = await api.post(`/nc/servers/${s.id}/favorite`);
      setFavs((f) => (r.data.favorited ? [...f, s.id] : f.filter((x) => x !== s.id)));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const register = async () => {
    if (!me) { toast.error("Sign in to register a server"); return; }
    setBusy(true);
    try {
      await api.post("/nc/servers", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Server registered and marked online");
      setShowForm(false);
      setForm({ name: "", address: "", region: "OCE", mode: "Hardcore RP", description: "", discord: "", age_rating: "16+", tags: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const filtered = (data?.servers || []).filter((s) =>
    (!fMode || s.mode === fMode) &&
    (!fAge || s.age_rating === fAge) &&
    (!fFavsOnly || favs.includes(s.id)) &&
    (!fSearch || (s.name + " " + (s.description || "") + " " + (s.tags || []).join(" ")).toLowerCase().includes(fSearch.toLowerCase())));

  return (
    <div data-testid="servers-tab" className="flex flex-col gap-8">
      {/* rent banner */}
      <div className="border border-nc-green/40 bg-nc-green/5 p-6 flex flex-col md:flex-row gap-5 items-start relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 bg-nc-green/10 blur-[80px] rounded-full pointer-events-none" />
        <Server className="w-6 h-6 text-nc-green shrink-0" strokeWidth={1.75} />
        <div className="flex-1 relative">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-green uppercase mb-1">Official hosting partner</p>
          <p className="font-sans font-bold uppercase text-lg text-zinc-50">Rent a New Citizen server — live in minutes</p>
          <p className="font-mono text-xs text-zinc-400 mt-2 leading-relaxed max-w-2xl">
            Our hosting partner 4NetPlayers runs one-click New Citizen servers: instant setup, DDoS protection, full mod
            support for everything you build in the Mod Hub, and a control panel your admins will actually understand.
          </p>
        </div>
        <a data-testid="rent-server-cta" href={RENT_URL} target="_blank" rel="noopener noreferrer" className="relative font-mono text-xs tracking-[0.25em] uppercase bg-nc-green text-black px-6 py-3.5 hover:bg-nc-green/85 transition-colors flex items-center gap-2 shrink-0">
          Rent a Server <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">Server Browser</p>
          {data && <span className="font-mono text-[9px] text-zinc-500">{data.count} registered</span>}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="server-register-open" onClick={() => (me ? setShowForm((s) => !s) : toast.error("Sign in to register a server"))} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-4 py-2.5 transition-colors flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Register Your Server
          </button>
          <Link data-testid="server-guide-link" to="/new-citizen/server-guide" className="font-mono text-[9px] tracking-[0.2em] uppercase border border-nc-green/40 text-nc-green hover:bg-nc-green/10 px-4 py-2.5 transition-colors flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Owner Guide
          </Link>
        </div>
      </div>

      {/* Steam master server list */}
      {steam && steam.configured && (
        <div data-testid="steam-servers" className="border border-[#1b2838] bg-[#101822]/60 p-5 flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#66c0f4] uppercase">Live on Steam // Master Server List</p>
            <span className="font-mono text-[9px] text-zinc-500">{steam.count} online · app {steam.app_id}</span>
            <span className="font-mono text-[8px] text-zinc-400 ml-auto">syncs every 60s</span>
          </div>
          {steam.count === 0 ? (
            <p className="font-mono text-[10px] text-zinc-500">No servers online right now — official servers appear here at launch.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {steam.servers.map((s, i) => (
                <div key={i} data-testid={`steam-server-${i}`} className="flex items-center gap-3 border border-white/10 bg-black/40 px-4 py-3 flex-wrap">
                  <Wifi className="w-3.5 h-3.5 text-nc-green" />
                  <span className="font-mono text-[11px] text-zinc-100 flex-1 min-w-40 truncate">{s.name}</span>
                  {s.map && <span className="font-mono text-[8px] uppercase border border-white/10 text-zinc-400 px-2 py-1">{s.map}</span>}
                  <span className="font-mono text-[9px] text-nc-green">{s.players}/{s.max_players}</span>
                  <a data-testid={`steam-join-${i}`} href={s.join_url} className="font-mono text-[8px] uppercase tracking-[0.15em] bg-[#1b2838] text-[#66c0f4] hover:bg-[#2a475e] px-3 py-2 transition-colors">Join via Steam</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* filter bar — browser v2 */}
      {data && data.servers.length > 0 && (
        <div data-testid="server-filters" className="flex flex-col gap-2 border border-white/10 bg-[#070707] p-4">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="filter-search" value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Search name, description, tags…"
              className="w-full bg-[#050505] border border-white/10 pl-9 pr-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" />
          </div>
          <div className="flex gap-1.5 flex-wrap items-center">
            <button data-testid="filter-mode-all" onClick={() => setFMode("")} className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors ${!fMode ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>All modes</button>
            {MODES.map((m) => (
              <button key={m} data-testid={`filter-mode-${m.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} onClick={() => setFMode(fMode === m ? "" : m)}
                className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors ${fMode === m ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>{m}</button>
            ))}
            <span className="w-px h-4 bg-white/10 mx-1" />
            {AGE_RATINGS.map((a) => (
              <button key={a} data-testid={`filter-age-${a.replace("+", "plus")}`} onClick={() => setFAge(fAge === a ? "" : a)}
                className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors ${fAge === a ? "border-amber-400 text-amber-300 bg-amber-400/10" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>{a}</button>
            ))}
            {me && (
              <button data-testid="filter-favs" onClick={() => setFFavsOnly((v) => !v)}
                className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors flex items-center gap-1 ${fFavsOnly ? "border-nc-orange text-nc-orange bg-nc-orange/10" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>
                <Star className={`w-3 h-3 ${fFavsOnly ? "fill-nc-orange" : ""}`} /> Favourites
              </button>
            )}
            <span className="ml-auto font-mono text-[8px] text-zinc-600">{filtered.length} shown</span>
          </div>
        </div>
      )}

      <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Community Registry</p>

      <AnimatePresence>
        {showForm && (
          <motion.div data-testid="server-register-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="border border-white/10 bg-[#070707] p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-hidden">
            <div><span className={labelCls}>Server name *</span><input data-testid="server-name" className={inputCls} value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><span className={labelCls}>Connect address *</span><input data-testid="server-address" className={inputCls} placeholder="123.45.67.89:30120" value={form.address} maxLength={120} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><span className={labelCls}>Region</span><select className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></div>
            <div><span className={labelCls}>Mode</span><select className={inputCls} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></div>
            <div><span className={labelCls}>Age rating</span><select data-testid="server-age" className={inputCls} value={form.age_rating} onChange={(e) => setForm({ ...form, age_rating: e.target.value })}>{AGE_RATINGS.map((a) => <option key={a}>{a}</option>)}</select></div>
            <div><span className={labelCls}>Tags (comma separated, max 4)</span><input data-testid="server-tags" className={inputCls} placeholder="economy, whitelist, custom cars" value={form.tags} maxLength={90} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Description</span><textarea rows={2} className={`${inputCls} resize-none`} value={form.description} maxLength={600} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Discord invite</span><input className={inputCls} placeholder="https://discord.gg/…" value={form.discord} maxLength={120} onChange={(e) => setForm({ ...form, discord: e.target.value })} /></div>
            <div className="sm:col-span-2 flex gap-3">
              <button data-testid="server-register-submit" disabled={busy || form.name.trim().length < 3 || form.address.trim().length < 4} onClick={register} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Server className="w-4 h-4" />} Register Server
              </button>
              <button onClick={() => setShowForm(false)} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-zinc-200 px-3">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!data ? (
        <div className="border border-white/10 p-10 text-center font-mono text-xs text-zinc-400">Querying the registry…</div>
      ) : data.servers.length === 0 ? (
        <div data-testid="servers-empty" className="border border-dashed border-white/15 p-12 text-center">
          <p className="font-mono text-xs text-zinc-500 leading-relaxed">No community servers registered yet. Official servers go live at launch — register yours now and it will be listed day one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((s, i) => (
            <div key={s.id} data-testid={`server-card-${i}`} className="relative border border-white/10 bg-black/40 p-5 flex flex-col gap-3 hover:border-nc-blue/30 transition-colors">
              <button data-testid={`server-fav-${i}`} onClick={() => toggleFav(s)} title="Favourite"
                className="absolute top-4 right-4 text-zinc-600 hover:text-nc-orange transition-colors">
                <Star className={`w-4 h-4 ${favs.includes(s.id) ? "fill-nc-orange text-nc-orange" : ""}`} />
              </button>
              <div className="flex items-center gap-2.5 flex-wrap pr-8">
                {s.online ? <Wifi className="w-4 h-4 text-nc-green" /> : <WifiOff className="w-4 h-4 text-zinc-400" />}
                <p className="font-sans font-bold text-sm text-zinc-50">{s.name}</p>
                {s.official && <span className="font-mono text-[7px] tracking-[0.2em] uppercase border border-nc-orange/50 text-nc-orange bg-nc-orange/5 px-1.5 py-0.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Official</span>}
                {s.verified && <span data-testid={`server-verified-${i}`} className="font-mono text-[7px] tracking-[0.2em] uppercase border border-nc-green/50 text-nc-green bg-nc-green/5 px-1.5 py-0.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified Owner</span>}
                <span className={`font-mono text-[7px] uppercase ml-auto ${s.online ? "text-nc-green" : "text-zinc-400"}`}>{s.online ? "Online" : "Offline"}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="font-mono text-[8px] uppercase border border-white/10 text-zinc-400 px-2 py-1">{s.region}</span>
                <span className="font-mono text-[8px] uppercase border border-white/10 text-zinc-400 px-2 py-1">{s.mode}</span>
                <span className="font-mono text-[8px] uppercase border border-amber-500/40 text-amber-300 px-2 py-1">{s.age_rating || "16+"}</span>
                {(s.tags || []).map((t) => <span key={t} className="font-mono text-[8px] uppercase border border-nc-blue/30 text-nc-blue px-2 py-1">{t}</span>)}
                <span className="font-mono text-[8px] text-zinc-500 px-2 py-1">{s.address}</span>
                {s.has_world && <span className="font-mono text-[8px] uppercase border border-nc-blue/30 text-nc-blue px-2 py-1">NCDB World</span>}
                {(s.favorites || 0) > 0 && <span className="font-mono text-[8px] uppercase text-nc-orange/80 px-2 py-1 flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-nc-orange/60 text-nc-orange/60" />{s.favorites}</span>}
              </div>
              {s.snapshot && (
                <div data-testid={`server-snapshot-${i}`} className="flex gap-2 flex-wrap border border-white/5 bg-black/30 px-3 py-2">
                  <span className="font-mono text-[9px] text-zinc-300">{s.snapshot.players}/{s.snapshot.max_players} in city</span>
                  {(s.snapshot.on_duty?.police ?? 0) > 0 && <span className="font-mono text-[9px] text-nc-blue">{s.snapshot.on_duty.police} cops</span>}
                  {(s.snapshot.on_duty?.ems ?? 0) > 0 && <span className="font-mono text-[9px] text-nc-green">{s.snapshot.on_duty.ems} medics</span>}
                  {(s.snapshot.on_duty?.fire ?? 0) > 0 && <span className="font-mono text-[9px] text-nc-orange">{s.snapshot.on_duty.fire} fire</span>}
                  {s.snapshot.weather && <span className="font-mono text-[9px] text-zinc-500">{s.snapshot.weather}</span>}
                  {s.snapshot.game_time && <span className="font-mono text-[9px] text-zinc-500">{s.snapshot.game_time}</span>}
                  {s.snapshot.queue > 0 && <span className="font-mono text-[9px] text-nc-orange">queue {s.snapshot.queue}</span>}
                </div>
              )}
              {s.description && <p className="font-mono text-[10px] text-zinc-500 leading-relaxed line-clamp-2">{s.description}</p>}
              <div className="flex items-center gap-2 mt-auto pt-1 flex-wrap">
                <button data-testid={`server-pulse-${i}`} onClick={() => setStatsFor(s)} className="font-mono text-[8px] uppercase border border-nc-green/40 text-nc-green hover:bg-nc-green/10 px-2 py-1.5 transition-colors flex items-center gap-1"><Activity className="w-3 h-3" /> City Pulse</button>
                {s.discord && <a href={s.discord} target="_blank" rel="noopener noreferrer" className="font-mono text-[8px] uppercase border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 px-2 py-1.5 transition-colors flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> Discord</a>}
                <button data-testid={`server-apply-${i}`} onClick={() => (me ? setWlFor(s) : toast.error("Sign in to apply for whitelist"))} className="font-mono text-[8px] uppercase border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 px-2 py-1.5 transition-colors">Apply for Whitelist</button>
                {me && s.verified && (me.id === s.owner_id || me.role === "admin") && (
                  <a data-testid={`server-manage-${i}`} href={`/admin/hub?server=${s.id}`} className="font-mono text-[8px] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-2 py-1.5 transition-colors">Manage</a>
                )}
                {me && !s.verified && (me.id === s.owner_id || !s.owner_id || me.role === "admin") && (
                  <button data-testid={`server-claim-${i}`} onClick={() => setClaimFor(s)} className="font-mono text-[8px] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-2 py-1.5 transition-colors flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Claim & Verify</button>
                )}
                {me && (me.id === s.owner_id || me.role === "admin") && (
                  <a data-testid={`server-loading-${i}`} href={`/cef/loading/${s.id}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 hover:border-white/40 px-2 py-1.5 transition-colors">Loading Screen</a>
                )}
                <ReportButton testid={`server-report-${i}`} target={`server "${s.name}"`} onReport={(reason) => api.post(`/nc/servers/${s.id}/report`, { reason })} />
                {me && (me.id === s.owner_id || me.role === "admin") && (
                  <>
                    <button data-testid={`server-ping-${i}`} onClick={async () => { await api.post(`/nc/servers/${s.id}/heartbeat`); toast.success("Marked online"); load(); }} className="font-mono text-[8px] uppercase border border-nc-green/40 text-nc-green px-2 py-1.5 hover:bg-nc-green/10 transition-colors">Ping Online</button>
                    <button data-testid={`server-delete-${i}`} onClick={async () => { await api.delete(`/nc/servers/${s.id}`); toast.success("Removed"); load(); }} className="font-mono text-[8px] uppercase border border-white/15 text-zinc-500 hover:text-red-400 px-2 py-1.5 transition-colors"><Trash2 className="w-3 h-3" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {claimFor && <ClaimModal server={claimFor} onClose={() => setClaimFor(null)} onDone={() => { load(); }} />}
        {wlFor && <WhitelistModal server={wlFor} onClose={() => setWlFor(null)} />}
        {statsFor && <StatsModal server={statsFor} onClose={() => setStatsFor(null)} />}
      </AnimatePresence>

      {/* hire specialist */}
      <div className="border border-nc-blue/30 bg-nc-blue/5 p-6 flex flex-col md:flex-row gap-5 items-start">
        <Wrench className="w-5 h-5 text-nc-blue shrink-0" strokeWidth={1.75} />
        <div className="flex-1">
          <p className="font-sans font-bold uppercase text-sm text-zinc-100">Running a serious community? Hire a specialist.</p>
          <p className="font-mono text-[10px] text-zinc-400 mt-2 leading-relaxed max-w-3xl">
            Identity-verified New Citizen devs and modders from our Contractor Network will build your custom scripts,
            liveries, MLOs and server mods — escrow-protected, source included.
          </p>
        </div>
        <Link to="/directory" className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue text-white px-5 py-3 hover:bg-nc-blue/80 transition-colors flex items-center gap-2 shrink-0">Hire a Specialist <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>
    </div>
  );
}

function CommunityTab({ me }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", playstyle: "Hardcore RP", region: "OCE", members: 5, discord: "", description: "", looking_for: "" });
  const load = () => api.get("/nc/communities").then((r) => setData(r.data)).catch(() => setData({ communities: [] }));
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!me) { toast.error("Sign in to advertise your community"); return; }
    setBusy(true);
    try {
      await api.post("/nc/communities", form);
      toast.success("Community listed — recruits incoming");
      setShowForm(false);
      setForm({ name: "", playstyle: "Hardcore RP", region: "OCE", members: 5, discord: "", description: "", looking_for: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="community-tab" className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Roleplay Community Board</p>
          <p className="font-mono text-[10px] text-zinc-500 mt-2 max-w-2xl leading-relaxed">Factions, departments, crews and RP groups recruiting members. Find your people — or put your community on the board.</p>
        </div>
        <button data-testid="community-post-open" onClick={() => (me ? setShowForm((s) => !s) : toast.error("Sign in to advertise your community"))} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-4 py-2.5 transition-colors flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Advertise Your Community
        </button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div data-testid="community-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="border border-white/10 bg-[#070707] p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-hidden">
            <div><span className={labelCls}>Community name *</span><input data-testid="community-name" className={inputCls} value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><span className={labelCls}>Playstyle</span><select className={inputCls} value={form.playstyle} onChange={(e) => setForm({ ...form, playstyle: e.target.value })}>{PLAYSTYLES.map((p) => <option key={p}>{p}</option>)}</select></div>
            <div><span className={labelCls}>Region</span><select className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></div>
            <div><span className={labelCls}>Current members</span><input type="number" min={1} className={inputCls} value={form.members} onChange={(e) => setForm({ ...form, members: parseInt(e.target.value) || 1 })} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Discord invite *</span><input data-testid="community-discord" className={inputCls} placeholder="https://discord.gg/…" value={form.discord} maxLength={120} onChange={(e) => setForm({ ...form, discord: e.target.value })} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Pitch * — who are you, what RP do you run?</span><textarea data-testid="community-description" rows={3} className={`${inputCls} resize-none`} value={form.description} maxLength={800} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Looking for</span><input className={inputCls} placeholder="e.g. experienced cops, EMS RPers, Aussie timezone players" value={form.looking_for} maxLength={200} onChange={(e) => setForm({ ...form, looking_for: e.target.value })} /></div>
            <div className="sm:col-span-2 flex gap-3">
              <button data-testid="community-submit" disabled={busy || form.name.trim().length < 3 || form.discord.trim().length < 4 || form.description.trim().length < 20} onClick={post} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Post to Board
              </button>
              <button onClick={() => setShowForm(false)} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-zinc-200 px-3">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!data ? (
        <div className="border border-white/10 p-10 text-center font-mono text-xs text-zinc-400">Loading the board…</div>
      ) : data.communities.length === 0 ? (
        <div data-testid="community-empty" className="border border-dashed border-white/15 p-12 text-center">
          <p className="font-mono text-xs text-zinc-500 leading-relaxed">The board is empty — be the first community recruiting for New Citizen.</p>
          <a href="https://discord.gg/NPhbFVSmpd" target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 mt-4 inline-flex items-center gap-2 transition-colors">
            Meanwhile, join the official Discord <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.communities.map((c, i) => (
            <motion.div key={c.id} data-testid={`community-card-${i}`} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3 hover:border-nc-orange/40 transition-colors" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-sans font-bold text-sm text-zinc-50">{c.name}</p>
                <span className="font-mono text-[8px] text-zinc-500 flex items-center gap-1 shrink-0"><Users className="w-3 h-3" /> {c.members}</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <span className="font-mono text-[7px] uppercase border border-nc-orange/40 text-nc-orange px-1.5 py-0.5">{c.playstyle}</span>
                <span className="font-mono text-[7px] uppercase border border-white/10 text-zinc-400 px-1.5 py-0.5">{c.region}</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-400 leading-relaxed line-clamp-3">{c.description}</p>
              {c.looking_for && <p className="font-mono text-[9px] text-zinc-500"><span className="text-nc-blue uppercase">Looking for:</span> {c.looking_for}</p>}
              <div className="flex items-center gap-2 mt-auto pt-1">
                <a data-testid={`community-join-${i}`} href={c.discord} target="_blank" rel="noopener noreferrer" className="font-mono text-[8px] uppercase tracking-[0.15em] bg-nc-blue hover:bg-nc-blue/80 text-white px-3 py-2 transition-colors flex items-center gap-1.5"><Globe className="w-3 h-3" /> Join</a>
                <ReportButton testid={`community-report-${i}`} target={`community "${c.name}"`} onReport={(reason) => api.post(`/nc/communities/${c.id}/report`, { reason })} />
                {me && (me.id === c.owner_id || me.role === "admin") && (
                  <button data-testid={`community-delete-${i}`} onClick={async () => { await api.delete(`/nc/communities/${c.id}`); toast.success("Removed"); load(); }} className="font-mono text-[8px] uppercase border border-white/15 text-zinc-500 hover:text-red-400 px-2 py-1.5 transition-colors"><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

const GUIDE_STEPS = [
  { t: "Option A — one-click hosted (recommended)", b: "Rent from our official partner 4NetPlayers: pick New Citizen, choose your region and slots, and the server deploys itself. Mods from the Mod Hub upload through their file manager. Best for communities that want uptime without maintenance.", link: RENT_URL },
  { t: "Option B — self-host the dedicated server", b: "Install SteamCMD, then download the New Citizen Dedicated Server app (free tool, anonymous login works). Extract to a folder with 20GB free. Hardware minimum: 4 cores, 8GB RAM, SSD. A home connection needs 10Mbit+ upload for 32 players." },
  { t: "Configure server.cfg", b: "Copy server.example.cfg to server.cfg. Set your server name (this is how the browser lists it), max players, region tag, and RCON password. Set sv_mode to hardcore-rp / casual-rp / cops-civs to match your community." },
  { t: "Open the ports", b: "Forward UDP 27015 (game) and UDP 27016 (query) on your router, or open them in your VPS firewall. No port forwarding = nobody can join, and the server never shows in the browser." },
  { t: "Install mods", b: "Drop Mod Hub exports into the server's Mods folder: lightbar CSVs into Mods/ELS, FGear INIs into Mods/Vehicles, mission JSON/Lua into Mods/Missions, skins + skin_manifest.json into Mods/Skins. Restart to load." },
  { t: "List it here", b: "Register your server on this page with its connect address. Then hit 'Claim & Verify' on your card: put the token in your server hostname, confirm with 2FA and the ownership agreement — you get the verified-owner badge, your server API key (X-NC-Key) for the bridge, and your own isolated world registry (NPCs, cases, player progress) plus a themed loading screen players see while joining." },
];

const GUIDELINES = [
  "Server names must not impersonate official New Citizen servers — 'Official' tags are granted by the studio only.",
  "RP communities set their own rules, but cheating tools, doxxing and hate content get servers delisted network-wide.",
  "Monetisation is allowed (priority queue, cosmetics) — selling gameplay advantages that break RP balance gets reviewed.",
  "Report servers through this page, not on Discord. Every report lands with the moderation team with evidence requirements.",
  "Mod content installed on your server must be your own work, Mod Hub community-shared work, or properly licensed.",
];

function GuideTab() {
  return (
    <div data-testid="guide-tab" className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
      <div className="flex flex-col gap-4">
        <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">Hosting Guide // Setup</p>
        {GUIDE_STEPS.map((s, i) => (
          <motion.div key={i} data-testid={`guide-step-${i}`} className="border border-white/10 bg-black/40 p-5 flex gap-4" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
            <span className="font-sans font-bold text-2xl text-nc-blue/60 shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <p className="font-sans font-bold text-sm text-zinc-100 mb-1.5">{s.t}</p>
              <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{s.b}</p>
              {s.link && <a href={s.link} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] uppercase text-nc-green hover:text-zinc-100 mt-2 inline-flex items-center gap-1 transition-colors">Open 4NetPlayers <ExternalLink className="w-3 h-3" /></a>}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="flex flex-col gap-4">
        <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Server Guidelines</p>
        <div className="border border-white/10 bg-[#070707] p-5 flex flex-col gap-3">
          {GUIDELINES.map((g, i) => (
            <p key={i} data-testid={`guideline-${i}`} className="font-mono text-[10px] text-zinc-400 leading-relaxed flex gap-2.5">
              <ShieldCheck className="w-3.5 h-3.5 text-nc-orange shrink-0 mt-0.5" /> {g}
            </p>
          ))}
        </div>
        <div className="border border-nc-blue/30 bg-nc-blue/5 p-5">
          <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
            <b className="text-zinc-100">Need custom content for your server?</b> Verified New Citizen specialists build
            to order — scripts, liveries, interiors. <Link to="/directory" className="text-nc-blue underline underline-offset-4">Contractor Network</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ServersHubPage() {
  const [tab, setTab] = useState("servers");
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null)); }, []);

  const TABS = [
    { id: "servers", label: "Server Browser", icon: Server },
    { id: "community", label: "Community Board", icon: Users },
    { id: "guide", label: "Host Your Own", icon: BookOpen },
  ];

  return (
    <div data-testid="servers-hub-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="New Citizen Servers — Rent, Browse & Report Servers, RP Communities"
        description="Rent an official-partner New Citizen server via 4NetPlayers, browse official and community servers, report rule-breakers, follow the hosting guide, and find hardcore RP communities recruiting members."
        path="/new-citizen/servers"
        keywords="New Citizen server hosting, rent New Citizen server, 4netplayers New Citizen, New Citizen RP community, New Citizen server browser, report server, dedicated server guide"
      />
      <SiteNav />
      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-3">New Citizen // Network</p>
            <h1 className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">Servers & Communities</h1>
            <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
              Rent a server in minutes, browse official and community worlds, find a hardcore RP crew recruiting right
              now — or learn to host your own city from scratch.
            </p>
            {me && (
              <a data-testid="servers-deck-cta" href="/admin/hub" className="mt-6 inline-flex items-center gap-2 bg-nc-orange hover:bg-nc-orange/80 text-black font-mono text-[10px] tracking-[0.25em] uppercase px-6 py-3.5 transition-colors">
                Open your Server Deck →
              </a>
            )}
          </motion.div>
        </div>
      </header>

      <div className="border-b border-white/10 bg-[#070707] sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`servers-tab-btn-${t.id}`} onClick={() => setTab(t.id)} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-3.5 border-b-2 transition-colors duration-200 flex items-center gap-2 whitespace-nowrap ${tab === t.id ? "border-nc-orange text-zinc-50 bg-zinc-900/60" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full flex-1">
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {tab === "servers" && <ServersTab me={me} />}
          {tab === "community" && <CommunityTab me={me} />}
          {tab === "guide" && <GuideTab />}
        </motion.div>
      </main>
      <SiteFooter />
      <NcdbDisclaimer />
    </div>
  );
}
